

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Icon,
    Snackbar,
    Alert,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import DataTable from "examples/Tables/DataTable";
import TableSkeleton from 'components/CustomSkeleton/TableSkeleton';
import EmptyState from 'components/EmptyTable/EmptyState';
import { useAuth } from 'auth/Auth';
import { usePermission } from 'auth/usePermission';
import NewAddonModal from 'components/NewAddonModal';

const Addons = () => {
    const [addons, setAddons] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const { axiosInstance } = useAuth();
    const navigate = useNavigate();
    const { canCreate } = usePermission('addons');

    useEffect(() => {
        fetchAddons();
    }, []);

    const fetchAddons = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get('/addons/');
            setAddons(response.data);
        } catch (error) {
            console.error('Error fetching addons:', error);
            setSnackbar({ open: true, message: 'Error loading addons', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    const handleSaveAddon = async (payload) => {
        try {
            await axiosInstance.post('/addons/', payload);
            setSnackbar({ open: true, message: 'Addon created successfully', severity: 'success' });
            fetchAddons();
        } catch (error) {
            console.error('Error saving addon:', error);
            throw error;
        }
    };

    const handleRowClick = (rowData) => {
        navigate(`/addons/${rowData.id}`);
    };

    const columns = [
        {
            Header: "Addon Name",
            accessor: "name",
            Cell: ({ value, row }) => (
                <MDTypography
                    component="span"
                    variant="caption"
                    color="info"
                    fontWeight="medium"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleRowClick(row.original)}
                >
                    {value}
                </MDTypography>
            )
        },
        { Header: "Type", accessor: "type" },
        {
            Header: "Trip",
            accessor: "trip_id",
            Cell: ({ row }) => row.original.trip?.name || row.original.trip_id || "-"
        },
        {
            Header: "Price",
            accessor: "addon_price",
            Cell: ({ value }) => value ? `${value}` : "0"
        },
        {
            Header: "HSS",
            accessor: "high_season_surcharge",
            Cell: ({ value }) => value ? `${value}` : "0"
        },
    ];

    const tableData = useMemo(() => ({
        columns,
        rows: addons,
    }), [addons]);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDTypography variant="h6" color="white">Addons</MDTypography>
                                <MDButton
                                    variant="gradient"
                                    color="dark"
                                    onClick={handleOpenModal}
                                    disabled={!canCreate}
                                >
                                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                                    &nbsp;Add Addon
                                </MDButton>
                            </MDBox>
                            <MDBox pt={3}>
                                {isLoading ? (
                                    <TableSkeleton />
                                ) : addons.length > 0 ? (
                                    <DataTable
                                        table={tableData}
                                        isSorted={true}
                                        entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                                        canSearch
                                        showTotalEntries
                                        noEndBorder
                                    />
                                ) : (
                                    <EmptyState moduleName="Addon" canCreate={canCreate} />
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            

            <NewAddonModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveAddon}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default Addons;
